import React, { createContext, useContext, useState, useCallback } from "react";
import { setProperty, deepCopy } from "../../../util/util";

// Create Context
const RecipeContext = createContext(undefined);

// Provider Component
export const RecipeProvider = ({ children }) => {
	const [recipes, setRecipes] = useState([]);
	const [selectedRecipe, setSelectedRecipe] = useState({});
	const [editingRecipe, setEditingRecipe] = useState({});
	const [editMode, setEditMode] = useState(false);

	// Actions – stable references to avoid "Maximum update depth" from consumers that depend on them (e.g. useEffect cleanup)
	const getRecipes = useCallback((data) => {
		setRecipes((prev) => [...prev, ...data]);
	}, []);

	const getMoreRecipes = useCallback((data) => {
		setRecipes((prev) => [...prev, ...data]);
	}, []);

	const getRecipeData = useCallback((data) => {
		setSelectedRecipe(data);
	}, []);

	const updateRecipe = useCallback(({ column, value }) => {
		setEditingRecipe((prev) => {
			const recipeData = deepCopy(prev);
			setProperty(recipeData, value, column);
			return recipeData;
		});
	}, []);

	const changeEditMode = useCallback(({ editMode: newEditMode, objectToEdit }) => {
		setEditMode(newEditMode);
		setEditingRecipe(JSON.parse(JSON.stringify(objectToEdit || {})));
	}, []);

	const setInitialEditingRecipe = useCallback((data) => {
		setEditingRecipe(data);
	}, []);

	// Context value
	const value = {
		recipes,
		selectedRecipe,
		editingRecipe,
		editMode,
		getRecipes,
		getMoreRecipes,
		getRecipeData,
		updateRecipe,
		changeEditMode,
		setInitialEditingRecipe,
	};

	return <RecipeContext.Provider value={value}>{children}</RecipeContext.Provider>;
};

// Custom Hook
export const useRecipe = () => {
	const context = useContext(RecipeContext);
	if (context === undefined) {
		throw new Error("useRecipe must be used within a RecipeProvider");
	}
	return context;
};
